import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { computed, makeObservable, observable } from 'mobx'

import { LearningProgressDetailColl } from './LearningProgressDetailColl'

// 교재 or 학습지 or 자기주도학습지

export const LEARNING_PROGRESS_TYPE = {
  WORKBOOK: 'workbook',
  WORKSHEET: 'worksheet',
  CHALLENGE: 'challenge',
  WRONG_AND_LEVEL_UP: 'wrong-and-level-up',
  CONCEPTUAL: 'conceptual',
  CAS: 'cas',
} as const

export class LearningProgress {
  title: string
  subtitle: string
  autoScorable: boolean
  elements: number[]
  bookType: common.BookType
  bookId: number
  round?: number
  studentNumber: number
  components?: LearningProgressDetailColl
  type?: common.LearningProgressType

  chapter?: string // 자기주도학습지일 경우 존재
  selfLearnings?: LearningProgress[] // 학습지일 경우, 자기주도학습지(자식학습지) 리스트
  selfLearningType?: common.SelfLearningType // 자기주도학습지일 경우, 자기주도학습지 타입

  constructor(params: res.LearningProgress | res.SelfLearningStudentWorksheet) {
    makeObservable(this, {
      title: observable,
      subtitle: observable,
      autoScorable: observable,
      elements: observable,
      bookType: observable,
      bookId: observable,
      round: observable,
      studentNumber: observable,
      components: observable,
      type: observable,
      chapter: observable,
      selfLearnings: observable,
      selfLearningType: observable,
      isChild: computed,
      isSelfLearning: computed,
      detailInfo: computed,
      elementsArr: computed,
      koBookType: computed,
      clientId: computed,
      uiTitle: computed,
      learningType: computed,
    })
    this.title = params.title
    this.autoScorable = !!params.autoScorable
    this.elements = params.elements
    this.bookType = params.bookType
    this.bookId = params.bookId
    this.studentNumber = params.studentNumber
    this.type = params.type

    if ('selfLearningType' in params) {
      this.selfLearningType = params.selfLearningType
      this.subtitle = ''
      this.chapter = params.chapter
    } else {
      this.round = params.round
      this.subtitle = params.subtitle ?? ''
    }

    if (params.components) {
      this.components = new LearningProgressDetailColl(params.components)
    }

    if (params.selfLearnings) {
      this.selfLearnings = params.selfLearnings.map((selfLearningWorksheet) =>
        params.type === 'CHALLENGE'
          ? new ChallengeSelfLearningProgress(selfLearningWorksheet)
          : new CustomSelfLearningProgress(selfLearningWorksheet),
      )
    }
  }

  get isChild() {
    return false
  }

  get isSelfLearning() {
    return this.type === 'CHALLENGE'
  }

  get detailInfo() {
    return this.components?.toArr[0]
  }

  get elementsArr() {
    return [...this.elements.values()]
  }

  get koBookType(): common.KoBookType {
    switch (this.learningType) {
      case LEARNING_PROGRESS_TYPE.WORKBOOK:
        return '교재'

      case LEARNING_PROGRESS_TYPE.CHALLENGE:
        return '챌린지'

      case LEARNING_PROGRESS_TYPE.CONCEPTUAL:
        return '챌린지 개념예제'

      case LEARNING_PROGRESS_TYPE.CAS:
        return tJosa('반응형평가')

      case LEARNING_PROGRESS_TYPE.WORKSHEET:
      default:
        return '학습지'
    }
  }

  get learningType(): common.LearningType {
    switch (this.bookType) {
      case 'WORKBOOK':
        return LEARNING_PROGRESS_TYPE.WORKBOOK

      case 'CAS':
        return LEARNING_PROGRESS_TYPE.CAS

      case 'WORKSHEET':
        // WORKSHEET일 때에만 존재하는 값
        switch (this.type) {
          case 'CHALLENGE':
            return LEARNING_PROGRESS_TYPE.CHALLENGE
          case 'CONCEPTUAL':
            return LEARNING_PROGRESS_TYPE.CONCEPTUAL
          default:
            return LEARNING_PROGRESS_TYPE.WORKSHEET
        }
    }
  }

  get clientId() {
    switch (this.bookType) {
      case 'WORKBOOK':
        return `${this.bookId}-${this.round}`
      case 'WORKSHEET':
      case 'CAS':
        return this.bookId
    }
  }

  // ui에 보여질 교재 or 학습지명
  get uiTitle() {
    return `${this.title} ${this.subtitle ? `- ${this.subtitle}` : ''}`
  }
}

export class ChallengeSelfLearningProgress extends LearningProgress {
  constructor(params: res.LearningProgress | res.SelfLearningStudentWorksheet) {
    super(params)
  }

  override get koBookType(): common.KoBookType {
    return '챌린지 오답'
  }

  override get isChild() {
    return true
  }

  override get isSelfLearning() {
    return true
  }

  override get learningType(): common.LearningType {
    return LEARNING_PROGRESS_TYPE.CHALLENGE
  }
}

export class CustomSelfLearningProgress extends LearningProgress {
  constructor(params: res.LearningProgress | res.SelfLearningStudentWorksheet) {
    super(params)
  }

  override get koBookType(): common.KoBookType {
    if (this.selfLearningType === 'LEVEL_UP') return '심화'
    if (this.selfLearningType === 'WRONG') return '오답'
    return '학습지'
  }

  override get isChild() {
    return true
  }

  override get isSelfLearning() {
    return true
  }

  override get learningType(): common.LearningType {
    return LEARNING_PROGRESS_TYPE.WRONG_AND_LEVEL_UP
  }
}
